import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useProjectStore } from '../../store/projectStore';
import { useUsersStore } from '../../store/usersStore';
import { useSitesStore } from '../../store/sitesStore';
import { Job, JobRecurrenceRule, Site } from '../../types/site';
import { formatRecurrenceLabel } from '../../lib/jobRecurrence';
import { isUnassignedSiteId, UNASSIGNED_JOB_SITE, UNASSIGNED_SITE_ID } from '../../lib/unassignedJobSite';
import {
  Search,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Repeat,
} from 'lucide-react';

type JobTableRow =
  | { kind: 'job'; job: Job; site: Site }
  | { kind: 'recurrence'; rule: JobRecurrenceRule; site: Site };

const JOB_TYPE_TABS = [
  { key: 'all', label: 'All Jobs' },
  { key: 'once-off', label: 'Once-Off' },
  { key: 'recurring', label: 'Recurring' },
  { key: 'recurring-masters', label: 'Recurring Masters' },
] as const;
type JobTypeTab = typeof JOB_TYPE_TABS[number]['key'];

const isJobRow = (row: JobTableRow): row is Extract<JobTableRow, { kind: 'job' }> =>
  row.kind === 'job';

const isOnceOffJobRow = (row: JobTableRow): boolean =>
  row.kind === 'job' && !row.job.isRecurringInstance && !row.job.recurrenceRuleId;

const isRecurringInstanceRow = (row: JobTableRow): boolean =>
  row.kind === 'job' && (!!row.job.isRecurringInstance || !!row.job.recurrenceRuleId);

interface JobsListViewProps {
  onJobClick: (site: Site, jobId: string) => void;
  onRecurringRuleClick?: (site: Site, ruleId: string) => void;
  onCreateJob?: () => void;
}

/** Fixed column layout — widths stay stable when recurring rows / badges appear */
const JOBS_TABLE_COLUMNS = [
  { key: 'check', width: 40 },
  { key: 'jobName', width: '28%' },
  { key: 'status', width: 108 },
  { key: 'siteName', width: '16%' },
  { key: 'dueDate', width: 132 },
  { key: 'assignedTo', width: '14%' },
  { key: 'lastUpdated', width: 120 },
] as const;

const cellTruncate = 'block truncate min-w-0';

const JOB_TYPE_TAB_KEYS = new Set<string>(JOB_TYPE_TABS.map((tab) => tab.key));

const filterStorageKey = (collectionId: string | null, name: string) =>
  `jobsManager_${collectionId ?? 'none'}_${name}`;

const legacyFilterStorageKey = (name: string) => `jobsManager_filter_${name}`;

const readStoredStringSet = (collectionId: string | null, name: string): Set<string> => {
  try {
    const raw = localStorage.getItem(filterStorageKey(collectionId, name))
      ?? localStorage.getItem(legacyFilterStorageKey(name));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === 'string' && value.length > 0));
  } catch {
    return new Set();
  }
};

const readStoredJobTypeTab = (collectionId: string | null): JobTypeTab => {
  try {
    const raw = localStorage.getItem(filterStorageKey(collectionId, 'jobType'));
    if (raw && JOB_TYPE_TAB_KEYS.has(raw)) return raw as JobTypeTab;
  } catch {
    /* ignore corrupt storage */
  }
  return 'all';
};

const readStoredSearchQuery = (collectionId: string | null): string => {
  try {
    return localStorage.getItem(filterStorageKey(collectionId, 'search')) ?? '';
  } catch {
    return '';
  }
};

const loadFiltersFromStorage = (collectionId: string | null) => ({
  sites: readStoredStringSet(collectionId, 'sites'),
  assignees: readStoredStringSet(collectionId, 'assignees'),
  states: readStoredStringSet(collectionId, 'states'),
  jobTypeTab: readStoredJobTypeTab(collectionId),
  searchQuery: readStoredSearchQuery(collectionId),
});

const persistFilterValue = (collectionId: string | null, name: string, value: string) => {
  const key = filterStorageKey(collectionId, name);
  if (localStorage.getItem(key) !== value) {
    localStorage.setItem(key, value);
  }
};

const setsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value));

const JobsListView: React.FC<JobsListViewProps> = ({
  onJobClick,
  onRecurringRuleClick,
  onCreateJob,
}) => {
  const { 
    currentProject,
    appTheme
  } = useProjectStore();
  const { sites, unassignedJobs, recurrenceRules, saveSiteJobs, saveUnassignedJobs, deleteRecurrenceRule, collectionId } = useSitesStore();
  const { users } = useUsersStore();
  const isDarkTheme = appTheme === 'dark';

  const [searchQuery, setSearchQuery] = useState(
    () => loadFiltersFromStorage(useSitesStore.getState().collectionId).searchQuery,
  );
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prevSortRef = useRef<{ column: string | null; direction: 'asc' | 'desc' }>({ column: null, direction: 'asc' });
  const [openFilter, setOpenFilter] = useState<'sites' | 'assignees' | 'state' | null>(null);
  const [selectedSites, setSelectedSites] = useState(
    () => loadFiltersFromStorage(useSitesStore.getState().collectionId).sites,
  );
  const [selectedAssignees, setSelectedAssignees] = useState(
    () => loadFiltersFromStorage(useSitesStore.getState().collectionId).assignees,
  );
  const [selectedStates, setSelectedStates] = useState(
    () => loadFiltersFromStorage(useSitesStore.getState().collectionId).states,
  );
  const [jobTypeTab, setJobTypeTab] = useState<JobTypeTab>(
    () => loadFiltersFromStorage(useSitesStore.getState().collectionId).jobTypeTab,
  );
  const sitesFilterRef = useRef<HTMLDivElement>(null);
  const assigneesFilterRef = useRef<HTMLDivElement>(null);
  const stateFilterRef = useRef<HTMLDivElement>(null);
  const loadedCollectionRef = useRef<string | null>(useSitesStore.getState().collectionId);

  useEffect(() => {
    if (loadedCollectionRef.current === collectionId) return;
    loadedCollectionRef.current = collectionId;
    const loaded = loadFiltersFromStorage(collectionId);
    setSelectedSites(loaded.sites);
    setSelectedAssignees(loaded.assignees);
    setSelectedStates(loaded.states);
    setJobTypeTab(loaded.jobTypeTab);
    setSearchQuery(loaded.searchQuery);
  }, [collectionId]);

  useEffect(() => {
    persistFilterValue(collectionId, 'sites', JSON.stringify(Array.from(selectedSites)));
  }, [collectionId, selectedSites]);

  useEffect(() => {
    persistFilterValue(collectionId, 'assignees', JSON.stringify(Array.from(selectedAssignees)));
  }, [collectionId, selectedAssignees]);

  useEffect(() => {
    persistFilterValue(collectionId, 'states', JSON.stringify(Array.from(selectedStates)));
  }, [collectionId, selectedStates]);

  useEffect(() => {
    persistFilterValue(collectionId, 'jobType', jobTypeTab);
  }, [collectionId, jobTypeTab]);

  useEffect(() => {
    persistFilterValue(collectionId, 'search', searchQuery);
  }, [collectionId, searchQuery]);

  const getRowKey = (row: JobTableRow): string => {
    if (row.kind === 'job') return `job:${row.site.id}::${row.job.id}`;
    return `rule:${row.rule.id}`;
  };

  const getSiteById = (siteId: string): Site | undefined => sites.find((s) => s.id === siteId);

  // Get all jobs and recurring rules from all sites
  const allRows = useMemo(() => {
    const rows: JobTableRow[] = [];

    sites.forEach((site) => {
      if (site.jobs?.length) {
        site.jobs.forEach((job: Job) => {
          rows.push({ kind: 'job', job, site });
        });
      }
    });

    unassignedJobs.forEach((job) => {
      rows.push({ kind: 'job', job, site: UNASSIGNED_JOB_SITE });
    });

    recurrenceRules.forEach((rule) => {
      const site = getSiteById(rule.siteId);
      if (site) {
        rows.push({ kind: 'recurrence', rule, site });
      }
    });

    return rows;
  }, [sites, unassignedJobs, recurrenceRules]);

  const jobTypeCounts = useMemo(() => {
    let onceOff = 0;
    let recurring = 0;
    let recurringMasters = 0;
    allRows.forEach((row) => {
      if (row.kind === 'recurrence') {
        recurringMasters++;
      } else if (isRecurringInstanceRow(row)) {
        recurring++;
      } else {
        onceOff++;
      }
    });
    return {
      all: onceOff + recurring,
      'once-off': onceOff,
      recurring,
      'recurring-masters': recurringMasters,
    };
  }, [allRows]);

  const visibleRows = useMemo(() => {
    switch (jobTypeTab) {
      case 'once-off':
        return allRows.filter(isOnceOffJobRow);
      case 'recurring':
        return allRows.filter(isRecurringInstanceRow);
      case 'recurring-masters':
        return allRows.filter((row) => row.kind === 'recurrence');
      case 'all':
      default:
        return allRows.filter(isJobRow);
    }
  }, [allRows, jobTypeTab]);

  // Filter option lists (scoped to job type tab, not secondary filters)
  const uniqueSites = useMemo(() => {
    const list = [...sites].sort((a, b) => a.name.localeCompare(b.name));
    if (unassignedJobs.length > 0) {
      return [UNASSIGNED_JOB_SITE, ...list];
    }
    return list;
  }, [sites, unassignedJobs]);

  const uniqueAssignees = useMemo(() => {
    const assignees = new Set<string>();
    users.forEach((u) => {
      if (u.displayName?.trim()) assignees.add(u.displayName.trim());
    });
    visibleRows.forEach((row) => {
      const assignedTo = row.kind === 'job' ? row.job.assignedTo : row.rule.assignedTo;
      if (assignedTo?.trim()) assignees.add(assignedTo.trim());
    });
    return Array.from(assignees).sort();
  }, [visibleRows, users]);

  const uniqueStates = useMemo(() => {
    const states = new Set<string>();
    visibleRows.forEach((row) => {
      if (row.kind === 'job' && row.job.status?.trim()) {
        states.add(row.job.status.trim());
      }
    });
    return Array.from(states).sort();
  }, [visibleRows]);

  useEffect(() => {
    const validSiteIds = new Set(sites.map((site) => site.id));
    if (unassignedJobs.length > 0) {
      validSiteIds.add(UNASSIGNED_SITE_ID);
    }
    setSelectedSites((prev) => {
      const next = new Set(Array.from(prev).filter((id) => validSiteIds.has(id)));
      return setsEqual(prev, next) ? prev : next;
    });
  }, [sites, unassignedJobs]);

  useEffect(() => {
    const validAssignees = new Set(uniqueAssignees);
    setSelectedAssignees((prev) => {
      const next = new Set(Array.from(prev).filter((assignee) => validAssignees.has(assignee)));
      return setsEqual(prev, next) ? prev : next;
    });
  }, [uniqueAssignees]);

  useEffect(() => {
    const validStates = new Set(uniqueStates);
    setSelectedStates((prev) => {
      const next = new Set(Array.from(prev).filter((state) => validStates.has(state)));
      return setsEqual(prev, next) ? prev : next;
    });
  }, [uniqueStates]);

  // Filter and sort rows
  const filteredRows = useMemo(() => {
    let result = visibleRows;
    
    // Apply site filter
    if (selectedSites.size > 0) {
      result = result.filter((row) => selectedSites.has(row.site.id));
    }
    
    // Apply assignee filter
    if (selectedAssignees.size > 0) {
      result = result.filter((row) => {
        const assignedTo = row.kind === 'job' ? row.job.assignedTo : row.rule.assignedTo;
        if (!assignedTo) return false;
        return selectedAssignees.has(assignedTo.trim());
      });
    }
    
    // Apply state filter (recurrence rows excluded when state filter is active)
    if (selectedStates.size > 0) {
      result = result.filter((row) => {
        if (row.kind === 'recurrence') return false;
        if (!row.job.status) return false;
        return selectedStates.has(row.job.status.trim());
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) => {
        const title = row.kind === 'job' ? row.job.title : row.rule.title;
        const assignedTo = row.kind === 'job' ? row.job.assignedTo : row.rule.assignedTo;
        return (
          title.toLowerCase().includes(query) ||
          row.site.name.toLowerCase().includes(query) ||
          (assignedTo && assignedTo.toLowerCase().includes(query))
        );
      });
    }
    
    // Apply sorting
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aValue: string | number = 0;
        let bValue: string | number = 0;
        
        switch (sortColumn) {
          case 'jobName':
            aValue = (a.kind === 'job' ? a.job.title : a.rule.title).toLowerCase();
            bValue = (b.kind === 'job' ? b.job.title : b.rule.title).toLowerCase();
            break;
          case 'siteName':
            aValue = a.site.name.toLowerCase();
            bValue = b.site.name.toLowerCase();
            break;
          case 'dueDate':
            aValue = a.kind === 'job'
              ? (a.job.dueDate
                ? (a.job.dueDate instanceof Date ? a.job.dueDate.getTime() : new Date(a.job.dueDate).getTime())
                : 0)
              : (a.rule.nextRunAt instanceof Date ? a.rule.nextRunAt.getTime() : new Date(a.rule.nextRunAt).getTime());
            bValue = b.kind === 'job'
              ? (b.job.dueDate
                ? (b.job.dueDate instanceof Date ? b.job.dueDate.getTime() : new Date(b.job.dueDate).getTime())
                : 0)
              : (b.rule.nextRunAt instanceof Date ? b.rule.nextRunAt.getTime() : new Date(b.rule.nextRunAt).getTime());
            break;
          case 'assignedTo':
            aValue = (a.kind === 'job' ? a.job.assignedTo || '' : a.rule.assignedTo || '').toLowerCase();
            bValue = (b.kind === 'job' ? b.job.assignedTo || '' : b.rule.assignedTo || '').toLowerCase();
            break;
          case 'lastUpdated':
            aValue = a.kind === 'job'
              ? (a.job.updatedAt instanceof Date ? a.job.updatedAt.getTime() : new Date(a.job.updatedAt).getTime())
              : (a.rule.updatedAt instanceof Date ? a.rule.updatedAt.getTime() : new Date(a.rule.updatedAt).getTime());
            bValue = b.kind === 'job'
              ? (b.job.updatedAt instanceof Date ? b.job.updatedAt.getTime() : new Date(b.job.updatedAt).getTime())
              : (b.rule.updatedAt instanceof Date ? b.rule.updatedAt.getTime() : new Date(b.rule.updatedAt).getTime());
            break;
          case 'status':
            aValue = a.kind === 'job' ? (a.job.status || '').toLowerCase() : 'recurring';
            bValue = b.kind === 'job' ? (b.job.status || '').toLowerCase() : 'recurring';
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [visibleRows, searchQuery, sortColumn, sortDirection, selectedSites, selectedAssignees, selectedStates]);
  
  const handleSort = (column: string) => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    const newColumn = sortColumn === column ? sortColumn : column;
    
    // Check if sort actually changed
    const sortChanged = prevSortRef.current.column !== newColumn || prevSortRef.current.direction !== newDirection;
    
    if (sortChanged) {
      setShouldAnimate(true);
      prevSortRef.current = { column: newColumn, direction: newDirection };
      
      // Reset animation flag after animation completes
      setTimeout(() => setShouldAnimate(false), 500);
    }
    
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={12} className="text-gray-300" />
      : <ArrowDown size={12} className="text-gray-300" />;
  };

  // Checkbox selection helpers
  const isAllSelected = filteredRows.length > 0 && selectedJobs.size === filteredRows.length;
  const isIndeterminate = selectedJobs.size > 0 && selectedJobs.size < filteredRows.length;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedJobs(new Set(filteredRows.map(getRowKey)));
    } else {
      setSelectedJobs(new Set());
    }
  };

  const handleSelectRow = (rowKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newSelected = new Set(selectedJobs);
    if (e.target.checked) {
      newSelected.add(rowKey);
    } else {
      newSelected.delete(rowKey);
    }
    setSelectedJobs(newSelected);
  };

  const handleDeleteSelected = () => {
    if (selectedJobs.size === 0 || !currentProject) return;
    
    const count = selectedJobs.size;
    const message = count === 1 
      ? 'Are you sure you want to delete this item? This action cannot be undone.'
      : `Are you sure you want to delete ${count} items? This action cannot be undone.`;
    
    if (window.confirm(message)) {
      const selectedRowKeys = Array.from(selectedJobs);
      const jobsBySite = new Map<string, Set<string>>();
      const ruleIds: string[] = [];

      selectedRowKeys.forEach((key) => {
        if (key.startsWith('rule:')) {
          ruleIds.push(key.slice(5));
          return;
        }
        if (!key.startsWith('job:')) return;
        const jobKey = key.slice(4);
        const separatorIndex = jobKey.indexOf('::');
        if (separatorIndex === -1) return;
        const siteId = jobKey.slice(0, separatorIndex);
        const jobId = jobKey.slice(separatorIndex + 2);
        if (!jobsBySite.has(siteId)) jobsBySite.set(siteId, new Set());
        jobsBySite.get(siteId)!.add(jobId);
      });

      jobsBySite.forEach((jobIds, siteId) => {
        if (isUnassignedSiteId(siteId)) {
          const updatedJobs = unassignedJobs.filter((job) => !jobIds.has(job.id));
          void saveUnassignedJobs(updatedJobs);
          return;
        }
        const site = sites.find((s) => s.id === siteId);
        if (!site) return;
        const updatedJobs = (site.jobs ?? []).filter((job) => !jobIds.has(job.id));
        void saveSiteJobs(siteId, updatedJobs);
      });

      ruleIds.forEach((ruleId) => {
        void deleteRecurrenceRule(ruleId);
      });

      setSelectedJobs(new Set());
    }
  };

  // Format date helper
  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Get date color based on due date
  const getDateColor = (date: Date | undefined): string => {
    if (!date) return 'text-gray-300';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'text-gray-300';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(d);
    dueDate.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      // Past date
      return 'text-red-400';
    } else if (diffDays === 0) {
      // Today
      return 'text-orange-400';
    } else if (diffDays === 1) {
      // Tomorrow
      return 'text-yellow-400';
    } else {
      // Future date
      return 'text-gray-300';
    }
  };

  // Format last updated helper
  const formatLastUpdated = (date: Date | undefined) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  // Filter handlers
  const handleToggleSite = (siteId: string) => {
    const newSelected = new Set(selectedSites);
    if (newSelected.has(siteId)) {
      newSelected.delete(siteId);
    } else {
      newSelected.add(siteId);
    }
    setSelectedSites(newSelected);
  };

  const handleToggleAssignee = (assignee: string) => {
    const newSelected = new Set(selectedAssignees);
    if (newSelected.has(assignee)) {
      newSelected.delete(assignee);
    } else {
      newSelected.add(assignee);
    }
    setSelectedAssignees(newSelected);
  };

  const handleToggleState = (state: string) => {
    const newSelected = new Set(selectedStates);
    if (newSelected.has(state)) {
      newSelected.delete(state);
    } else {
      newSelected.add(state);
    }
    setSelectedStates(newSelected);
  };

  const handleSelectAllSites = () => {
    if (selectedSites.size === uniqueSites.length) {
      setSelectedSites(new Set());
    } else {
      setSelectedSites(new Set(uniqueSites.map(site => site.id)));
    }
  };

  const handleSelectAllAssignees = () => {
    if (selectedAssignees.size === uniqueAssignees.length) {
      setSelectedAssignees(new Set());
    } else {
      setSelectedAssignees(new Set(uniqueAssignees));
    }
  };

  const handleSelectAllStates = () => {
    if (selectedStates.size === uniqueStates.length) {
      setSelectedStates(new Set());
    } else {
      setSelectedStates(new Set(uniqueStates));
    }
  };

  // Close filter dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (openFilter === 'sites' && sitesFilterRef.current && !sitesFilterRef.current.contains(target)) {
        setOpenFilter(null);
      } else if (openFilter === 'assignees' && assigneesFilterRef.current && !assigneesFilterRef.current.contains(target)) {
        setOpenFilter(null);
      } else if (openFilter === 'state' && stateFilterRef.current && !stateFilterRef.current.contains(target)) {
        setOpenFilter(null);
      }
    };

    if (openFilter) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openFilter]);

  const border = isDarkTheme ? '#343a43' : '#e5e7eb';
  const muted = isDarkTheme ? '#9ca3af' : '#6b7280';

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${isDarkTheme ? 'bg-[#171a1f]' : 'bg-gray-900'}`}>
      {/* Header Section */}
      <div className={`${isDarkTheme ? 'bg-[#20242b] border-b border-[#343a43]' : 'bg-gray-800 border-b border-gray-700'} px-4 py-2`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-white">Job Manager</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Total ({filteredRows.length})
            </span>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <button
            onClick={() => {
              if (onCreateJob) {
                onCreateJob();
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-xs font-medium"
          >
            <Plus size={14} />
            Add
          </button>
          
          <button
            onClick={handleDeleteSelected}
            disabled={selectedJobs.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors text-xs font-medium"
          >
            <Trash2 size={14} />
            Delete
          </button>

          {/* Filter Buttons */}
          <div className="flex items-center gap-1.5">
            {/* Sites Filter */}
            <div className="relative" ref={sitesFilterRef}>
              <button
                onClick={() => setOpenFilter(openFilter === 'sites' ? null : 'sites')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors text-xs font-medium ${
                  selectedSites.size > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
              >
                Sites
                <span className={`px-1.5 rounded text-[10px] min-w-[20px] text-center leading-none ${
                  selectedSites.size > 0 ? 'bg-white/20 py-0.5' : 'opacity-0 py-0.5'
                }`}>
                  {selectedSites.size > 0 ? selectedSites.size : '0'}
                </span>
                <ChevronDown size={12} className={openFilter === 'sites' ? 'rotate-180' : ''} />
              </button>
              {openFilter === 'sites' && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-50 max-h-80 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-300">Filter by Site</span>
                    <button
                      onClick={handleSelectAllSites}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      {selectedSites.size === uniqueSites.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar">
                    {uniqueSites.length === 0 ? (
                      <div className="p-3 text-xs text-gray-400 text-center">No sites available</div>
                    ) : (
                      uniqueSites.map(site => (
                        <label
                          key={site.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSites.has(site.id)}
                            onChange={() => handleToggleSite(site.id)}
                            className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-300 flex-1">{site.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Assignees Filter */}
            <div className="relative" ref={assigneesFilterRef}>
              <button
                onClick={() => setOpenFilter(openFilter === 'assignees' ? null : 'assignees')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors text-xs font-medium ${
                  selectedAssignees.size > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
              >
                Assignees
                <span className={`px-1.5 rounded text-[10px] min-w-[20px] text-center leading-none ${
                  selectedAssignees.size > 0 ? 'bg-white/20 py-0.5' : 'opacity-0 py-0.5'
                }`}>
                  {selectedAssignees.size > 0 ? selectedAssignees.size : '0'}
                </span>
                <ChevronDown size={12} className={openFilter === 'assignees' ? 'rotate-180' : ''} />
              </button>
              {openFilter === 'assignees' && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-50 max-h-80 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-300">Filter by Assignee</span>
                    <button
                      onClick={handleSelectAllAssignees}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      {selectedAssignees.size === uniqueAssignees.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar">
                    {uniqueAssignees.length === 0 ? (
                      <div className="p-3 text-xs text-gray-400 text-center">No assignees available</div>
                    ) : (
                      uniqueAssignees.map(assignee => (
                        <label
                          key={assignee}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAssignees.has(assignee)}
                            onChange={() => handleToggleAssignee(assignee)}
                            className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-300 flex-1">{assignee}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* State Filter */}
            <div className="relative" ref={stateFilterRef}>
              <button
                onClick={() => setOpenFilter(openFilter === 'state' ? null : 'state')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors text-xs font-medium ${
                  selectedStates.size > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
              >
                State
                <span className={`px-1.5 rounded text-[10px] min-w-[20px] text-center leading-none ${
                  selectedStates.size > 0 ? 'bg-white/20 py-0.5' : 'opacity-0 py-0.5'
                }`}>
                  {selectedStates.size > 0 ? selectedStates.size : '0'}
                </span>
                <ChevronDown size={12} className={openFilter === 'state' ? 'rotate-180' : ''} />
              </button>
              {openFilter === 'state' && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-50 max-h-80 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-300">Filter by State</span>
                    <button
                      onClick={handleSelectAllStates}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      {selectedStates.size === uniqueStates.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar">
                    {uniqueStates.length === 0 ? (
                      <div className="p-3 text-xs text-gray-400 text-center">No states available</div>
                    ) : (
                      uniqueStates.map(state => (
                        <label
                          key={state}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStates.has(state)}
                            onChange={() => handleToggleState(state)}
                            className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-300 flex-1 capitalize">{state.replace('-', ' ')}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex-1 max-w-xs ml-auto">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Job type filter tabs */}
        <div className="flex items-center gap-1.5 pb-1 flex-shrink-0">
          {JOB_TYPE_TABS.map((tab) => {
            const count = jobTypeCounts[tab.key] ?? 0;
            const isActive = jobTypeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setJobTypeTab(tab.key)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: isActive ? (isDarkTheme ? '#374151' : '#1f2937') : 'transparent',
                  color: isActive ? '#ffffff' : muted,
                  border: `1px solid ${isActive ? (isDarkTheme ? '#4b5563' : '#374151') : border}`,
                }}
              >
                {tab.label}
                <span
                  className="px-1.5 py-px rounded-full text-[10px] font-semibold"
                  style={{
                    backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : (isDarkTheme ? '#374151' : '#e5e7eb'),
                    color: isActive ? '#fff' : muted,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-auto custom-scrollbar">
        {filteredRows.length > 0 ? (
            <table className="w-full table-fixed" style={{ borderCollapse: 'collapse' }}>
              <colgroup>
                {JOBS_TABLE_COLUMNS.map(col => (
                  <col
                    key={col.key}
                    style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }}
                  />
                ))}
              </colgroup>
              <thead className={`${isDarkTheme ? 'bg-[#252a32]' : 'bg-gray-800'} sticky top-0 z-10`}>
                <tr>
                  <th className="px-3 py-2 text-left border-b border-gray-700">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('jobName')}
                  >
                    <div className="flex items-center gap-1">
                      Job Name
                      {getSortIcon('jobName')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      State
                      {getSortIcon('status')}
                    </div>
                  </th>
                <th 
                  className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                  onClick={() => handleSort('siteName')}
                >
                  <div className="flex items-center gap-1">
                    Site Name
                    {getSortIcon('siteName')}
                  </div>
                </th>
                <th 
                  className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                  onClick={() => handleSort('dueDate')}
                >
                  <div className="flex items-center gap-1">
                    Due Date
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th 
                  className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                  onClick={() => handleSort('assignedTo')}
                >
                  <div className="flex items-center gap-1">
                    Assigned To
                    {getSortIcon('assignedTo')}
                  </div>
                </th>
                <th 
                  className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                  onClick={() => handleSort('lastUpdated')}
                >
                  <div className="flex items-center gap-1">
                    Last Updated
                    {getSortIcon('lastUpdated')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700" style={{ transition: 'all 0.3s ease-in-out' }}>
              {filteredRows.map((row, index) => {
                const rowKey = getRowKey(row);
                const isSelected = selectedJobs.has(rowKey);
                const isRecurrence = row.kind === 'recurrence';

                const handleRowClick = () => {
                  if (isRecurrence) {
                    onRecurringRuleClick?.(row.site, row.rule.id);
                    return;
                  }
                  onJobClick(row.site, row.job.id);
                };

                const title = isRecurrence ? row.rule.title : row.job.title;
                const assignedTo = isRecurrence ? row.rule.assignedTo : row.job.assignedTo;
                const dueDate = isRecurrence ? row.rule.nextRunAt : row.job.dueDate;
                const updatedAt = isRecurrence ? row.rule.updatedAt : row.job.updatedAt;

                return (
                  <tr
                    key={rowKey}
                    onClick={handleRowClick}
                    className={`hover:bg-gray-800 cursor-pointer transition-colors ${isSelected ? 'bg-gray-800' : ''} ${isRecurrence ? 'bg-purple-500/5' : ''}`}
                    style={{ 
                      ...(shouldAnimate && {
                        transition: 'all 0.5s ease-in-out',
                        transitionDelay: `${index * 20}ms`,
                        animation: 'fadeInUp 0.5s ease-out'
                      })
                    }}
                  >
                    <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectRow(rowKey, e)}
                        className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-1.5 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full overflow-hidden">
                          <span className="text-sm font-medium text-white truncate" title={title}>{title}</span>
                          {!isRecurrence && row.job.isRecurringInstance && (
                            <span
                              className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-blue-400/70 text-[10px] font-semibold leading-none text-blue-400 bg-blue-500/10"
                              title="Recurring job instance"
                            >
                              R
                            </span>
                          )}
                        </span>
                        {isRecurrence && (
                          <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-purple-500/20 text-purple-300">
                            <Repeat size={11} className="shrink-0" strokeWidth={2.5} />
                            {formatRecurrenceLabel(row.rule.intervalCount, row.rule.intervalUnit)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 overflow-hidden">
                      {isRecurrence ? (
                        <span className="inline-flex w-full max-w-full justify-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-purple-500/20 text-purple-300">
                          Recurring
                        </span>
                      ) : (
                        <span className={`inline-flex w-full max-w-full justify-center px-2 py-0.5 rounded-full text-xs font-medium capitalize whitespace-nowrap ${
                          row.job.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          row.job.status === 'in-progress' ? 'bg-blue-500/20 text-blue-400' :
                          row.job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {row.job.status ? row.job.status.replace('-', ' ') : '-'}
                        </span>
                      )}
                    </td>
                  <td className="px-3 py-1.5 overflow-hidden">
                      <span className={`inline-flex max-w-full px-2 py-0.5 rounded-full text-xs font-medium border border-gray-600 text-gray-300 ${cellTruncate}`} title={row.site.name}>
                        {row.site.name}
                      </span>
                  </td>
                  <td className="px-3 py-1.5 overflow-hidden">
                    <span
                      className={`text-sm ${getDateColor(dueDate)} ${cellTruncate}`}
                      title={isRecurrence ? `Next: ${formatDate(dueDate)}` : formatDate(dueDate)}
                    >
                      {isRecurrence ? `Next: ${formatDate(dueDate)}` : formatDate(dueDate)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 overflow-hidden">
                      {assignedTo ? (
                        <span className={`inline-flex max-w-full px-2 py-0.5 rounded-full text-xs font-medium border border-gray-600 text-gray-300 ${cellTruncate}`} title={assignedTo}>
                          {assignedTo}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-300">-</span>
                      )}
                  </td>
                  <td className="px-3 py-1.5 overflow-hidden">
                    <span className={`text-sm text-gray-300 ${cellTruncate}`} title={formatLastUpdated(updatedAt)}>
                      {formatLastUpdated(updatedAt)}
                    </span>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <p className="text-sm mb-1">
                {searchQuery ? 'No jobs found matching your search' : 'No jobs found'}
              </p>
              {!searchQuery && (
                <p className="text-xs text-gray-500">Create jobs from the Sites page</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsListView;

